<template>
  <div>
        <CCLFReport_SummaryAnalysis
          v-if="activeTab === 'summary-analysis'"
          :entity-id="effectiveEntityId"
          :performance-year="performanceYear"
          :loading="summaryLoading"
          :summary-data="summaryData"
          :attribution-series="attributionSeries"
          :report-dts="reportDts"
          :selected-report-dt="selectedReportDt"
          :scope-type="scopeType"
          :scope-entity-id="scopeEntityId"
          :scope-entities="scopeEntities"
          :care-setting="careSetting"
          @update:scope-type="scopeType = $event"
          @update:scope-entity-id="scopeEntityId = $event"
          @update:care-setting="careSetting = $event"
          @update:selected-report-dt="selectedReportDt = $event"
        />
        <CCLFReport_PatientReport
          v-if="activeTab === 'patient-report'"
          :contract-id="contractId"
          :performance-year="performanceYear"
          :dosthru="dosthru"
          :entity-id="entityId"
        />
        <CCLFReport_HCCGaps
          v-if="activeTab === 'hcc-gaps'"
          :contract-id="contractId"
          :performance-year="performanceYear"
          :dosthru="dosthru"
          :entity-id="entityId"
        />
        <CCLFReport_PatientVisits
          v-if="activeTab === 'patient-visits'"
          :contract-id="contractId"
          :performance-year="performanceYear"
          :dosthru="dosthru"
          :entity-id="entityId"
        />
        <CCLFReport_AWVSummary
          v-if="activeTab === 'awv-summary'"
          :contract-id="contractId"
          :performance-year="performanceYear"
          :dosthru="dosthru"
          :entity-id="entityId"
        />
  </div>
</template>

<script>
import CCLFReport_SummaryAnalysis from './CCLFReport_SummaryAnalysis.vue'
import CCLFReport_PatientReport from './CCLFReport_PatientReport.vue'
import CCLFReport_HCCGaps from './CCLFReport_HCCGaps.vue'
import CCLFReport_PatientVisits from './CCLFReport_PatientVisits.vue'
import CCLFReport_AWVSummary from './CCLFReport_AWVSummary.vue'

export default {
  name: 'CCLFReport',
  components: { CCLFReport_SummaryAnalysis, CCLFReport_PatientReport, CCLFReport_HCCGaps, CCLFReport_PatientVisits, CCLFReport_AWVSummary },
  props: {
    initialEntityId: { type: [Number, String], default: null },
    activeTab: { type: String, default: 'summary-analysis' },
  },

  data() {
    return {
      contractId: 'A5069',
      performanceYear: 2026,
      dosthru: null,
      entityId: this.initialEntityId ? Number(this.initialEntityId) : null,
      summaryData: null,  // {chart, table, panel, max_efctv_dt}
      summaryLoading: false,
      // Populated from /report-dates/ on mount (loadReportDates). Seeded empty
      // so a new CMS quarter appears without a code change — this was a
      // hardcoded list that silently omitted 2026Q2 once it loaded.
      selectedReportDt: '',
      reportDts: [],
      attributionSeries: [],
      scopeType: '',
      scopeEntityId: null,
      scopeEntities: [],
      careSetting: '',
      exporting: false,
    }
  },

  computed: {
    effectiveEntityId() { return this.scopeEntityId || this.entityId },
  },

  watch: {
    initialEntityId(val) { this.entityId = val ? Number(val) : null },
    performanceYear() { this.loadSummary(); this.loadAttribution() },
    entityId() { this.loadScopeEntities() },
    effectiveEntityId() { this.loadSummary(); this.loadAttribution() },
    scopeType() { this.scopeEntityId = null; this.loadScopeEntities() },
    careSetting() { this.loadSummary() },
  },

  mounted() { this.loadReportDates() },

  methods: {
    async loadReportDates() {
      try {
        const resp = await this.$axios.post('/api/claim-explorer/report-dates/', { contract_id: this.contractId })
        // endpoint returns a bare array of 'YYYY-MM-DD', newest first
        const dates = (resp.data || []).map(d => String(d).slice(0, 10)).sort()
        this.reportDts = dates
        if (!this.selectedReportDt || !dates.includes(this.selectedReportDt)) {
          this.selectedReportDt = dates[dates.length - 1] || ''
        }
      } catch {
        this.reportDts = []
      }
      this.loadSummary()
      this.loadAttribution()
    },

    async loadScopeEntities() {
      if (!this.scopeType || !this.entityId) { this.scopeEntities = []; return }
      try {
        const resp = await this.$axios.get('/api/entities/', { params: { ancestor_ids: this.entityId, entity_type_name: this.scopeType, page_size: 2000 } })
        this.scopeEntities = (resp.data?.results || resp.data || []).map(e => ({ id: e.id, name: e.name })).sort((a, b) => a.name.localeCompare(b.name))
      } catch { this.scopeEntities = [] }
    },

    async loadAttribution() {
      try {
        const resp = await this.$axios.post('/api/impact_analytics/composable-metrics/606/run/', { entity_id: this.effectiveEntityId, time_series: true })
        this.attributionSeries = ((resp.data || {}).results || []).filter(r => r.attribution != null).map(r => ({ date: r.observation_date, value: r.attribution })).sort((a, b) => a.date > b.date ? 1 : -1)
      } catch { this.attributionSeries = [] }
    },

    async loadSummary() {
      if (!this.contractId) return
      this.summaryLoading = true
      try {
        const resp = await this.$axios.post('/api/claim-explorer/summary/', {
          entity_id: this.effectiveEntityId,
          contract_id: this.contractId,
          care_setting: this.careSetting || undefined,
          selected_report_dt: this.selectedReportDt,
        })
        this.summaryData = resp.data
        if (Array.isArray(resp.data?.report_dts)) {
          this.reportDts = resp.data.report_dts.slice().sort()
          if (!this.reportDts.includes(this.selectedReportDt)) {
            this.selectedReportDt = this.reportDts[this.reportDts.length - 1] || ''
          }
        }
        if (Array.isArray(resp.data?.attribution_series)) {
          this.attributionSeries = resp.data.attribution_series.slice()
        }
      } catch (e) {
        console.error('Failed to load summary:', e)
        this.summaryData = null
      } finally {
        this.summaryLoading = false
      }
    },

    async exportXlsx() {
      if (!this.contractId || !this.performanceYear) return
      this.exporting = true
      try {
        const response = await this.$axios.post('/api/portfolio_management/cclf-report/export-xlsx/',
          { contract_id: this.contractId, performance_year: this.performanceYear, dosthru: this.dosthru, entity_id: this.entityId },
          { responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url; link.setAttribute('download', `cclf_monthly_report_${this.contractId}_${this.performanceYear}.xlsx`)
        document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url)
      } catch (error) { console.error('Failed to export XLSX:', error) }
      finally { this.exporting = false }
    },
  },
}
</script>

<style scoped>
@media print {
  :deep(button), :deep(select), :deep(nav) { display: none !important; }
  :deep([style*="height: 600px"]), :deep([style*="height: 400px"]) { height: auto !important; overflow: visible !important; }
}
</style>
